import React, { useEffect, useState } from "react"
import { graphql } from "gatsby"
import sanitizeHtml from "sanitize-html"
import { getStatus, LuasStatusLine } from "../../api/luas-forecasts-api"
import {
  getLuasLineDisplayName,
  getLuasLineStatusLabel,
  getLuasUiLabels,
  LuasUiLabelsRecord,
} from "../luas/luas-ui-labels"
import { mockLuasStatusLines, mockLuasTravelUpdateHtml } from "../luas/luas-status-mock"
import * as styles from "./luas-travel-update-module.module.scss"

type RenderProps = {
  label?: string | null
  locale?: string
  luasUiLabels?: LuasUiLabelsRecord | null
}

type Status = {
  lines: LuasStatusLine[]
  travelUpdateHtml: string
}

const sanitizeOptions = {
  allowedTags: ["p", "br", "b", "strong", "i", "em", "ul", "ol", "li", "a", "span"],
  allowedAttributes: { a: ["href"] },
}

const LuasTravelUpdateModule: React.FC<RenderProps> = ({ label, luasUiLabels }) => {
  const uiLabels = getLuasUiLabels(luasUiLabels)
  const [status, setStatus] = useState<Status | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    getStatus()
      .then(result => {
        if (!cancelled) setStatus({ lines: result.lines, travelUpdateHtml: result.travelUpdateHtml })
      })
      .catch(fetchError => {
        console.error("[luas] Status API unavailable", fetchError)
        if (cancelled) return
        // The live API blocks CORS on localhost; show mock data there so the design can still be previewed.
        if (process.env.NODE_ENV === "development" && window.location.hostname === "localhost") {
          setStatus({ lines: mockLuasStatusLines, travelUpdateHtml: mockLuasTravelUpdateHtml })
        } else {
          setError(uiLabels.travelUpdatesUnavailable)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [uiLabels.travelUpdatesUnavailable])

  return (
    <section className={styles.page} aria-label={label || uiLabels.travelUpdates}>
      <h2 className={styles.title}>{label || uiLabels.travelUpdates}</h2>

      <div>
        {loading && (
          <p className={styles.loading} role="status" aria-live="polite" aria-atomic="true">
            {uiLabels.loadingTravelUpdates}
          </p>
        )}
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        {status && (
          <>
            <ul className={styles.lineStatuses} aria-live="polite">
              {status.lines.map(line => (
                <li key={line.name} className={styles.lineStatus}>
                  <span
                    className={`${styles.lineName} ${line.name.toLowerCase().includes("red") ? styles.redLine : styles.greenLine}`}
                  >
                    {getLuasLineDisplayName(line.name, uiLabels)}
                  </span>
                  <span className={styles.lineMessage}>
                    {line.linewideMessage || getLuasLineStatusLabel(line.operatingNormally, uiLabels)}
                  </span>
                  <span className={line.operatingNormally ? styles.badgeNormal : styles.badgeDisrupted}>
                    {getLuasLineStatusLabel(line.operatingNormally, uiLabels)}
                  </span>
                </li>
              ))}
            </ul>

            {status.travelUpdateHtml && (
              <div className={styles.bulletinWrapper}>
                <h3 className={styles.bulletinHeading}>{uiLabels.latestUpdate}</h3>
                <div
                  className={styles.bulletin}
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(status.travelUpdateHtml, sanitizeOptions) }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </section>
  )
}

export const fragments = graphql`
  fragment LuasTravelUpdateModuleFields on DatoCmsLuasTravelUpdateModule {
    __typename
    id
    label
  }
`

export default LuasTravelUpdateModule
